/**
 * Accessibility mixin for Vue components
 * Provides common accessibility functionality for EAA compliance
 */

import { keyboardManager, FocusManager, AriaManager } from '@/utils/accessibility.utils';

export default {
    data() {
        return {
            // Component-specific accessibility state
            a11y: {
                announcements: [],
                focusableElements: [],
                trapFocusCleanup: null
            }
        };
    },

    mounted() {
        // Auto-register escape key handler if component has handleEscape method
        if (typeof this.handleEscape === 'function') {
            this.registerEscapeHandler();
        }

        // Auto-register keyboard shortcuts if component defines them
        if (this.keyboardShortcuts) {
            this.registerKeyboardShortcuts();
        }

        // Initialize focus management
        this.initializeFocusManagement();
    },

    beforeDestroy() {
        // Cleanup accessibility event handlers
        this.cleanupAccessibility();
    },

    methods: {
        /**
         * Register escape key handler for this component
         * @param {number} priority - Higher priority handlers execute first
         */
        registerEscapeHandler(priority = 0) {
            const componentId = this.getComponentId();
            keyboardManager.registerEscapeCallback(
                componentId,
                this.handleEscape.bind(this),
                priority
            );
        },

        /**
         * Register keyboard shortcuts defined in component
         */
        registerKeyboardShortcuts() {
            if (!this.keyboardShortcuts) return;

            Object.entries(this.keyboardShortcuts).forEach(([shortcut, method]) => {
                if (typeof this[method] === 'function') {
                    keyboardManager.registerShortcut(shortcut, this[method].bind(this));
                }
            });
        },

        /**
         * Initialize focus management for the component
         */
        initializeFocusManagement() {
            // Store focusable elements for quick access
            this.$nextTick(() => {
                this.a11y.focusableElements = FocusManager.getFocusableElements(this.$el);
            });
        },

        /**
         * Set focus to element with accessibility features
         * @param {Element|string} target - Element or selector
         * @param {Object} options - Focus options
         */
        setAccessibleFocus(target, options = {}) {
            return FocusManager.setFocus(target, {
                preventScroll: false,
                ...options
            });
        },

        /**
         * Trap focus within this component (useful for modals)
         * @param {Element} container - Container to trap focus in (defaults to component root)
         */
        trapFocus(container = this.$el) {
            if (this.a11y.trapFocusCleanup) {
                this.a11y.trapFocusCleanup();
            }
            this.a11y.trapFocusCleanup = FocusManager.trapFocus(container);
        },

        /**
         * Release focus trap
         */
        releaseFocusTrap() {
            if (this.a11y.trapFocusCleanup) {
                this.a11y.trapFocusCleanup();
                this.a11y.trapFocusCleanup = null;
            }
        },

        /**
         * Announce message to screen readers
         * @param {string} message - Message to announce
         * @param {string} priority - 'polite' or 'assertive'
         */
        announceToScreenReader(message, priority = 'polite') {
            FocusManager.announceToScreenReader(message, priority);
            
            // Store announcement for component state
            this.a11y.announcements.push({
                message,
                priority,
                timestamp: Date.now()
            });
        },

        /**
         * Update ARIA attributes on element
         * @param {Element|string} target - Element or selector
         * @param {Object} attributes - ARIA attributes to set
         */
        updateAriaAttributes(target, attributes) {
            const element = typeof target === 'string' ? this.$el.querySelector(target) : target;
            if (element) {
                AriaManager.setAttributes(element, attributes);
            }
        },

        /**
         * Handle keyboard navigation for lists/grids
         * @param {KeyboardEvent} event 
         * @param {Array} items - Array of focusable items
         * @param {number} currentIndex - Current focused item index
         * @param {Object} options - Navigation options
         */
        handleArrowKeyNavigation(event, items, currentIndex, options = {}) {
            const { 
                orientation = 'vertical', // 'vertical', 'horizontal', or 'grid'
                columnsPerRow = 1,
                wrapAround = true,
                onSelectionChange = null
            } = options;

            let newIndex = currentIndex;

            switch (event.key) {
                case 'ArrowDown':
                    if (orientation === 'vertical' || orientation === 'grid') {
                        newIndex = orientation === 'grid' 
                            ? Math.min(currentIndex + columnsPerRow, items.length - 1)
                            : currentIndex + 1;
                    }
                    break;

                case 'ArrowUp':
                    if (orientation === 'vertical' || orientation === 'grid') {
                        newIndex = orientation === 'grid'
                            ? Math.max(currentIndex - columnsPerRow, 0)
                            : currentIndex - 1;
                    }
                    break;

                case 'ArrowRight':
                    if (orientation === 'horizontal' || orientation === 'grid') {
                        newIndex = currentIndex + 1;
                    }
                    break;

                case 'ArrowLeft':
                    if (orientation === 'horizontal' || orientation === 'grid') {
                        newIndex = currentIndex - 1;
                    }
                    break;

                case 'Home':
                    newIndex = 0;
                    break;

                case 'End':
                    newIndex = items.length - 1;
                    break;

                default:
                    return false; // Key not handled
            }

            // Apply wrap-around logic
            if (wrapAround) {
                if (newIndex < 0) newIndex = items.length - 1;
                if (newIndex >= items.length) newIndex = 0;
            } else {
                newIndex = Math.max(0, Math.min(newIndex, items.length - 1));
            }

            // Only proceed if index changed
            if (newIndex !== currentIndex) {
                event.preventDefault();
                
                // Focus new item
                if (items[newIndex]) {
                    this.setAccessibleFocus(items[newIndex]);
                }

                // Call selection change callback
                if (onSelectionChange) {
                    onSelectionChange(newIndex, items[newIndex]);
                }

                return true;
            }

            return false;
        },

        /**
         * Get unique component ID for accessibility registration
         * @returns {string}
         */
        getComponentId() {
            return `${this.$options.name || 'component'}-${this._uid}`;
        },

        /**
         * Cleanup accessibility resources
         */
        cleanupAccessibility() {
            const componentId = this.getComponentId();
            
            // Unregister escape handler
            keyboardManager.unregisterEscapeCallback(componentId);

            // Unregister keyboard shortcuts
            if (this.keyboardShortcuts) {
                Object.keys(this.keyboardShortcuts).forEach(shortcut => {
                    keyboardManager.unregisterShortcut(shortcut);
                });
            }

            // Release focus trap
            this.releaseFocusTrap();

            // Clear accessibility state
            this.a11y = {
                announcements: [],
                focusableElements: [],
                trapFocusCleanup: null
            };
        },

        /**
         * Check if reduced motion is preferred
         * @returns {boolean}
         */
        prefersReducedMotion() {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        },

        /**
         * Check if high contrast is preferred
         * @returns {boolean}
         */
        prefersHighContrast() {
            return window.matchMedia('(prefers-contrast: high)').matches;
        },

        /**
         * Get current color scheme preference
         * @returns {string} 'light', 'dark', or 'no-preference'
         */
        getColorSchemePreference() {
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
            if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
            return 'no-preference';
        }
    },

    /**
     * Computed properties for accessibility state
     */
    computed: {
        /**
         * Check if component currently has focus trap active
         * @returns {boolean}
         */
        hasFocusTrap() {
            return !!this.a11y.trapFocusCleanup;
        },

        /**
         * Get recent announcements (last 5)
         * @returns {Array}
         */
        recentAnnouncements() {
            return this.a11y.announcements
                .slice(-5)
                .sort((a, b) => b.timestamp - a.timestamp);
        }
    }
};